import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// =========================================================
// HOVER MOSAIC TRANSITION
// =========================================================
export const initPopularGamesHoverMosaic = ({
  tileContainer,
  hoveredGame,
  activeHoverImageRef,
  hoverAnimationIdRef,
}) => {
  if (!tileContainer) return;

  const tiles = Array.from(
    tileContainer.querySelectorAll(".popular-games-hover-tile")
  );

  if (!tiles.length) return;

  const animationId = ++hoverAnimationIdRef.current;

  gsap.killTweensOf(tiles);

  const setTileImage = (image) => {
    tiles.forEach((tile) => {
      tile.style.backgroundImage = `url(${image})`;
    });
  };

  const revealTiles = (image) => {
    if (animationId !== hoverAnimationIdRef.current) return;

    setTileImage(image);

    gsap.set(tiles, {
      opacity: 0,
      scale: 0.72,
      x: () => gsap.utils.random(-45, 45),
      y: () => gsap.utils.random(-35, 35),
      rotation: () => gsap.utils.random(-5, 5),
    });

    gsap.to(tiles, {
      opacity: 0.95,
      scale: 1,
      x: 0,
      y: 0,
      rotation: 0,
      duration: 0.01,
      ease: "power3.out",
      stagger: {
        each: 0.002,
        from: "random",
      },
    });
  };

  const hideTiles = () => {
    gsap.to(tiles, {
      opacity: 0,
      scale: 0.88,
      x: () => gsap.utils.random(-20, 20),
      y: () => gsap.utils.random(-15, 15),
      rotation: () => gsap.utils.random(-2, 2),
      duration: 0.01,
      ease: "power2.in",
      stagger: {
        each: 0.012,
        from: "random",
      },
    });
  };

  if (!hoveredGame) {
    activeHoverImageRef.current = null;
    hideTiles();

    return () => gsap.killTweensOf(tiles);
  }

  const previousImage = activeHoverImageRef.current;
  const nextImage = hoveredGame.image;

  if (!previousImage) {
    activeHoverImageRef.current = nextImage;
    revealTiles(nextImage);
  } else if (previousImage !== nextImage) {
    activeHoverImageRef.current = nextImage;

    gsap.to(tiles, {
      opacity: 0,
      scale: 0.86,
      x: () => gsap.utils.random(-25, 25),
      y: () => gsap.utils.random(-20, 20),
      rotation: () => gsap.utils.random(-3, 3),
      duration: 0.22,
      ease: "power2.in",
      stagger: {
        each: 0.008,
        from: "random",
      },
      onComplete: () => {
        if (animationId !== hoverAnimationIdRef.current) return;
        revealTiles(nextImage);
      },
    });
  }

  return () => gsap.killTweensOf(tiles);
};

// =========================================================
// POPULAR + LIVE SCROLL ANIMATIONS
// =========================================================
export const initPopularGamesAnimations = ({
  sectionRef,
  liveSectionRef,
  setHoveredGame,
}) => {
  const section = sectionRef.current;
  const liveSection = liveSectionRef.current;

  if (!section || !liveSection) return;

  const context = gsap.context(() => {
    // =====================================================
    // POPULAR ELEMENTS
    // =====================================================
    const popularContent = section.querySelector(".popular-games-content");
    const hoverBackground = section.querySelector(
      ".popular-games-hover-background"
    );
    const eyebrow = section.querySelector(".popular-games-eyebrow");
    const titleLetters = section.querySelectorAll(
      ".popular-games-title-letter"
    );
    const link = section.querySelector(".popular-games-link");
    const cards = section.querySelectorAll(".popular-games-card");
    const defaultBackground = section.querySelector(
      ".popular-games-default-background"
    );

    // =====================================================
    // TRANSITION ELEMENTS
    // =====================================================
    const liveTransition = section.querySelector(".live-transition-tab");
    const liveTransitionDot = section.querySelector(".live-transition-dot");
    const liveTransitionLabel = section.querySelector(
      ".live-transition-label"
    );
    const liveTransitionText = section.querySelector(".live-transition-text");
    const liveTransitionArrow = section.querySelector(
      ".live-transition-arrow"
    );

    // =====================================================
    // LIVE ELEMENTS
    // =====================================================
    const liveEyebrow = liveSection.querySelector(".live-draws-eyebrow");
    const liveTitle = liveSection.querySelector(".live-draws-title");
    const liveLink = liveSection.querySelector(".live-draws-link");
    const liveCards = liveSection.querySelectorAll(
      ".live-draw-card-wrapper"
    );

    if (
      !popularContent ||
      !hoverBackground ||
      !eyebrow ||
      !titleLetters.length ||
      !link ||
      !cards.length ||
      !defaultBackground ||
      !liveTransition ||
      !liveTransitionDot ||
      !liveTransitionLabel ||
      !liveTransitionText ||
      !liveTransitionArrow ||
      !liveEyebrow ||
      !liveTitle ||
      !liveLink ||
      !liveCards.length
    ) {
      return;
    }

    // =====================================================
    // INITIAL POPULAR REVEAL STATE
    // =====================================================
    gsap.set(defaultBackground, {
      opacity: 0,
    });

    gsap.set(eyebrow, {
      x: -80,
      opacity: 0,
    });

    gsap.set(titleLetters, {
      y: 35,
      opacity: 0,
    });

    gsap.set(link, {
      y: 40,
      opacity: 0,
    });

    gsap.set(cards, {
      y: 100,
      opacity: 0,
      pointerEvents: "none",
    });

    // =====================================================
    // INITIAL TRANSITION STATE
    // =====================================================
    gsap.set(liveTransition, {
      position: "relative",
      top: "auto",
      left: "auto",
      width: "100%",
      height: "auto",
      x: 0,
      y: 0,
      scale: 1,
      opacity: 1,
      borderRadius: "20px",
      zIndex: 100,
    });

    gsap.set(liveTransitionDot, {
      scale: 1,
    });

    gsap.set(liveTransitionLabel, {
      fontSize: "",
      letterSpacing: "",
    });

    gsap.set(liveTransitionText, {
      y: 0,
      opacity: 1,
    });

    gsap.set(liveTransitionArrow, {
      x: 0,
      y: 0,
      scale: 0.75,
      opacity: 0,
      transformOrigin: "left center",
    });

    // =====================================================
    // INITIAL LIVE STATE
    // =====================================================

    // The complete Live section is initially invisible.
    // It will fade in based on its actual scroll position.
    gsap.set(liveSection, {
      opacity: 0,
    });

    gsap.set(liveEyebrow, {
      x: -80,
      opacity: 0,
    });

    gsap.set(liveTitle, {
      y: 35,
      opacity: 0,
    });

    gsap.set(liveLink, {
      y: 40,
      opacity: 0,
    });

    gsap.set(liveCards, {
      y: 100,
      scale: 0.94,
      opacity: 0,
    });

    // =====================================================
    // POPULAR REVEAL
    //
    // Scroll controlled and short.
    // =====================================================
    const popularRevealTimeline = gsap.timeline({
      paused: true,
    });

    popularRevealTimeline
      .to(
        defaultBackground,
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
        },
        0
      )
      .to(
        eyebrow,
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
        },
        0
      )
      .to(
        titleLetters,
        {
          y: 0,
          opacity: 1,
          duration: 0.55,
          stagger: 0.045,
          ease: "power3.out",
        },
        0
      )
      .to(
        link,
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
        },
        0
      )
      .to(
        cards,
        {
          y: 0,
          opacity: 1,
          duration: 0.85,
          stagger: 0.12,
          ease: "power3.out",
        },
        0.12
      );

    let popularRevealFinished = false;

    const popularRevealTrigger = ScrollTrigger.create({
      trigger: section,
      start: "top 65%",
      end: "top 5%",
      scrub: 0.7,
      invalidateOnRefresh: true,

      onUpdate: (self) => {
        if (popularRevealFinished) return;

        popularRevealTimeline.progress(self.progress);

        if (self.progress >= 0.999) {
          popularRevealFinished = true;

          popularRevealTimeline.progress(1);

          gsap.set(cards, {
            pointerEvents: "auto",
          });

          self.kill();
        }
      },
    });

    // =====================================================
    // POPULAR → LIVE TRANSITION
    //
    // This is the only fancy transition.
    // =====================================================
    let hasPlayedOnce = false;

    const transitionTimeline = gsap.timeline({
      paused: true,
    });

    // -----------------------------------------------------
    // PHASE 1
    // Popular content exits.
    // -----------------------------------------------------
    transitionTimeline
      .to(
        eyebrow,
        {
          x: -80,
          opacity: 0,
          duration: 0.3,
          ease: "power3.in",
        },
        0
      )
      .to(
        titleLetters,
        {
          y: -35,
          opacity: 0,
          duration: 0.3,
          stagger: {
            each: 0.018,
            from: "end",
          },
          ease: "power3.in",
        },
        0
      )
      .to(
        link,
        {
          y: -40,
          opacity: 0,
          duration: 0.3,
          ease: "power3.in",
        },
        0
      )
      .to(
        cards,
        {
          y: -100,
          opacity: 0,
          duration: 0.42,
          stagger: 0.055,
          ease: "power3.inOut",
        },
        0.02
      )
      .to(
        defaultBackground,
        {
          opacity: 0,
          duration: 0.4,
          ease: "power2.in",
        },
        0.05
      )
      .to(
        hoverBackground,
        {
          opacity: 0,
          duration: 0.4,
          ease: "power2.in",
        },
        0.05
      );

    transitionTimeline.set(
      cards,
      {
        pointerEvents: "none",
      },
      0.25
    );

    transitionTimeline.call(
      () => {
        setHoveredGame(null);
      },
      [],
      0.2
    );

    // -----------------------------------------------------
    // PHASE 2
    // Transition text → arrow.
    // -----------------------------------------------------
    transitionTimeline
      .to(
        liveTransitionText,
        {
          opacity: 0,
          y: -20,
          duration: 0.22,
          ease: "power2.in",
        },
        0.18
      )
      .to(
        liveTransitionArrow,
        {
          opacity: 0.7,
          scale: 1,
          x: 0,
          y: 0,
          duration: 0.3,
          ease: "back.out(1.5)",
        },
        0.25
      );

    // -----------------------------------------------------
    // PHASE 3
    // Compact LIVE tab expands across the pinned section.
    // -----------------------------------------------------
    transitionTimeline.to(
      liveTransition,
      {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        x: 0,
        y: 0,
        scale: 1,
        borderRadius: "0px",
        opacity: 1,
        zIndex: 100,
        duration: 0.55,
        ease: "power3.inOut",
      },
      0.32
    );

    // -----------------------------------------------------
    // PHASE 4
    // LIVE grows.
    // -----------------------------------------------------
    transitionTimeline
      .to(
        liveTransitionLabel,
        {
          fontSize: "clamp(48px, 8vw, 120px)",
          letterSpacing: "-0.04em",
          duration: 0.42,
          ease: "power3.out",
        },
        0.42
      )
      .to(
        liveTransitionDot,
        {
          scale: 1.6,
          duration: 0.35,
          ease: "power2.out",
        },
        0.42
      )
      .to(
        liveTransitionArrow,
        {
          x: "32vw",
          scale: 4.5,
          opacity: 0,
          duration: 0.45,
          ease: "power3.in",
        },
        0.55
      );

    // -----------------------------------------------------
    // PHASE 5
    // Live Games reveal behind the transition.
    // -----------------------------------------------------
    transitionTimeline
      .to(
        liveSection,
        {
          opacity: 1,
          duration: 0.4,
        },
        1.3
      )
      .to(
        liveEyebrow,
        {
          x: 0,
          opacity: 1,
          duration: 0.4,
          ease: "power3.out",
        },
        1.3
      )
      .to(
        liveTitle,
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          ease: "power3.out",
        },
        1.35
      )
      .to(
        liveLink,
        {
          y: 0,
          opacity: 1,
          duration: 0.4,
          ease: "power3.out",
        },
        1.35
      )
      .to(
        liveCards,
        {
          y: 0,
          scale: 1,
          opacity: 1,
          duration: 0.5,
          stagger: 0.08,
          ease: "power3.out",
        },
        1.4
      );

    // -----------------------------------------------------
    // PHASE 6
    // Transition disappears.
    // -----------------------------------------------------
    transitionTimeline.to(
      liveTransition,
      {
        opacity: 0,
        duration: 0.3,
        ease: "power2.out",
      },
      1.0
    );

    // =====================================================
    // PINNED SCROLLTRIGGER
    //
    // Popular section itself is pinned.
    // =====================================================
    const transitionTrigger = ScrollTrigger.create({
      trigger: section,
      start: "top 60px",
      end: () => `+=${window.innerHeight * 2.5}`, // Increased scroll distance for slower transition
      pin: true,
      pinSpacing: false,
      scrub: 0.4,
      invalidateOnRefresh: true,

      onUpdate: (self) => {
        if (hasPlayedOnce) return;

        transitionTimeline.progress(self.progress);

        if (self.progress >= 0.999) {
          hasPlayedOnce = true;

          transitionTimeline.progress(1);

          // =================================================
          // FINAL NORMAL STATE
          // =================================================

          // Popular is restored for normal scrolling back up.
          gsap.set(popularContent, {
            opacity: 1,
          });

          gsap.set(defaultBackground, {
            opacity: 1,
          });

          gsap.set(hoverBackground, {
            opacity: 1,
          });

          gsap.set(eyebrow, {
            x: 0,
            opacity: 1,
          });

          gsap.set(titleLetters, {
            y: 0,
            opacity: 1,
          });

          gsap.set(link, {
            y: 0,
            opacity: 1,
          });

          gsap.set(cards, {
            y: 0,
            scale: 1,
            opacity: 1,
            pointerEvents: "auto",
          });

          // Transition is permanently hidden.
          gsap.set(liveTransition, {
            position: "relative",
            top: "auto",
            left: "auto",
            width: "100%",
            height: "auto",
            x: 0,
            y: 0,
            scale: 1,
            opacity: 0,
            borderRadius: "20px",
            zIndex: 10,
          });

          gsap.set(liveTransitionDot, {
            scale: 1,
          });

          gsap.set(liveTransitionLabel, {
            fontSize: "",
            letterSpacing: "",
          });

          gsap.set(liveTransitionText, {
            y: 0,
            opacity: 1,
          });

          gsap.set(liveTransitionArrow, {
            x: 0,
            y: 0,
            scale: 0.75,
            opacity: 0,
          });

          // Live content is ready, but the Live SECTION
          // itself controls its visibility based on scroll.
          gsap.set(liveEyebrow, {
            x: 0,
            opacity: 1,
          });

          gsap.set(liveTitle, {
            y: 0,
            opacity: 1,
          });

          gsap.set(liveLink, {
            y: 0,
            opacity: 1,
          });

          gsap.set(liveCards, {
            y: 0,
            scale: 1,
            opacity: 1,
          });

          // =================================================
          // The fancy transition is now finished forever.
          // =================================================
          self.kill();
        }
      },
    });

    // =====================================================
    // LIVE SECTION OPACITY
    //
    // The section continues to scroll normally.
    //
    // 80% → opacity starts
    // 50% → opacity reaches 100%
    // =====================================================
    const liveOpacityTrigger = ScrollTrigger.create({
      trigger: liveSection,
      start: "top 80%",
      end: "top 50%",
      scrub: 0.5,
      invalidateOnRefresh: true,

      onUpdate: (self) => {
        if (!hasPlayedOnce) return;
        gsap.set(liveSection, {
          opacity: self.progress,
        });
      },

      onLeave: () => {
        if (!hasPlayedOnce) return;
        gsap.set(liveSection, {
          opacity: 1,
        });
      },

      onLeaveBack: () => {
        if (!hasPlayedOnce) return;
        gsap.set(liveSection, {
          opacity: 0,
        });
      },
    });


    const livePinTrigger = ScrollTrigger.create({
      trigger: liveSection,
      start: "top 60px",
      end: () => `+=${window.innerHeight * 1.5}`, 
      pin: true,
      pinSpacing: true, 
      invalidateOnRefresh: true,
    });

    // =====================================================
    // REFRESH AFTER LAYOUT IS READY
    // =====================================================
    requestAnimationFrame(() => {
      ScrollTrigger.refresh();
    });
  });

  return () => context.revert();
};